/**
 * Tutorial Storage - Per-user localStorage management
 * Stores seen tutorial state with user ID prefix
 */

const STORAGE_PREFIX = 'gofood_tutorial_seen_'

/**
 * Get the storage key for a specific user and tutorial
 * @param {string} userId - User ID
 * @param {string} tutorialId - Tutorial ID
 * @returns {string} Storage key
 */
function getStorageKey(userId, tutorialId) {
  return `${STORAGE_PREFIX}${userId}_${tutorialId}`
}

/**
 * Check if a tutorial has been seen by a specific user
 * @param {string} userId - User ID
 * @param {string} tutorialId - Tutorial ID
 * @returns {boolean} Whether the tutorial has been seen
 */
export function isSeenByUser(userId, tutorialId) {
  if (!userId || !tutorialId) return false
  try {
    const key = getStorageKey(userId, tutorialId)
    return localStorage.getItem(key) === 'true'
  } catch {
    return false
  }
}

/**
 * Mark a tutorial as seen by a specific user
 * @param {string} userId - User ID
 * @param {string} tutorialId - Tutorial ID
 */
export function markSeenByUser(userId, tutorialId) {
  if (!userId || !tutorialId) return
  try {
    const key = getStorageKey(userId, tutorialId)
    localStorage.setItem(key, 'true')
  } catch {
    // localStorage not available or full
  }
}

/**
 * Clear all tutorial seen states for a specific user
 * @param {string} userId - User ID
 */
export function clearAllForUser(userId) {
  if (!userId) return
  try {
    const prefix = `${STORAGE_PREFIX}${userId}_`
    const keysToRemove = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith(prefix)) {
        keysToRemove.push(key)
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key))
  } catch {
    // localStorage not available
  }
}

/**
 * Get all seen tutorial IDs for a specific user
 * @param {string} userId - User ID
 * @returns {string[]} Array of seen tutorial IDs
 */
export function getSeenTutorials(userId) {
  if (!userId) return []
  try {
    const prefix = `${STORAGE_PREFIX}${userId}_`
    const seen = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith(prefix) && localStorage.getItem(key) === 'true') {
        seen.push(key.substring(prefix.length))
      }
    }
    return seen
  } catch {
    return []
  }
}
